/**
 * RESPONSIBILITY: one section of the Visit page — chosen from a list of templates.
 *
 * It began as one shape, a picture and the words beside it with an optional link, and that
 * shape is still what a section is unless somebody picks another: see visit-themes.ts. The
 * template is the only field always asked for and it decides every other one, the way an
 * event's band does - pick Facts and you are asked for facts and nothing else.
 *
 * Hiding a field never clears it, so changing template and back loses nothing.
 *
 * ROWS, NOT PROSE THAT GETS PARSED: a row says what it is. The picture is an @Img, uploaded
 * and sized like every other picture on the site; the side it sits on is the row's position
 * (or the template, if one says so), which an editor changes by dragging.
 */
import type { ImageValue } from '@framework/core';
import {
  Row, Text, Paragraph, Img, Link, Bool, Choice, List, RenderOf,
} from '@framework/core/decorators';
import { VISIT_THEMES, VISIT_THEME as T, DEFAULT_VISIT_THEME } from './visit-themes';
import { FactRow } from '../../events/rows/fact';
import { FaqRow } from '../../events/rows/faq';
import { VisitButton } from './visit-button';
import { VisitPictureRow } from './visit-picture';

const WITH_IMAGE = [T.Alternating, T.Image, T.ImageLeft, T.ImageRight, T.ImageTop, T.TextTop];
const WITH_WORDS = [T.Alternating, T.Text, T.ImageLeft, T.ImageRight, T.ImageTop, T.TextTop, T.Cta, T.Location];
const WITH_BUTTONS = [T.Text, T.ImageLeft, T.ImageRight, T.ImageTop, T.TextTop, T.Facts, T.Cta, T.Location];

@Row()
export class VisitBand {
  @Choice({
    label: 'Template', options: VISIT_THEMES, default: DEFAULT_VISIT_THEME,
    description:
      'Choose this first — it decides what you are asked for. '
      + 'Image and text, alternating: a picture and its words, swapping sides down the page. '
      + 'Text: one box of words. Image: one picture. '
      + 'Image left, text right · Text left, image right · Image on top · Text on top: a picture and words, arranged as it says. '
      + 'Facts: short labelled lines, like service times. '
      + 'Call to action: a bold band with words and buttons. '
      + 'Location: an address with a map link. '
      + 'Questions and answers: a list people can open. '
      + 'Video: a YouTube video. Gallery: a grid of pictures.',
  })
  theme!: string;

  @Img({
    label: 'Picture', folder: 'visit', maxSizeMb: 12, size: '1200 × 900',
    showWhen: { field: 'theme', is: WITH_IMAGE },
    description: 'Sits beside the words, or above or below them, as the template says.',
  })
  image!: ImageValue;

  @Text({ label: 'Title', maxLength: 200 })
  heading!: string;

  @Paragraph({
    label: 'Words', maxLength: 4000, rows: 4, markdown: true, allow: ['verse'],
    showWhen: { field: 'theme', is: WITH_WORDS },
  })
  body!: string;

  @RenderOf('body')
  bodyHtml!: string;

  /** The ORIGINAL single link. Only the first template has one; every other has buttons. */
  @Text({
    label: 'Link text', maxLength: 120, description: 'Optional. e.g. Children’s ministry.',
    showWhen: { field: 'theme', is: [T.Alternating] },
  })
  linkLabel!: string;

  @Link({
    label: 'Link', description: 'Where the link goes. Empty leaves the link off.',
    showWhen: { field: 'theme', is: [T.Alternating] },
  })
  linkHref!: string;

  @List({
    label: 'Buttons', of: () => VisitButton, maxItems: 4,
    showWhen: { field: 'theme', is: WITH_BUTTONS },
    description: 'The first is the main button; the rest are quieter.',
  })
  buttons!: VisitButton[];

  @List({
    label: 'Facts', of: () => FactRow, maxItems: 12,
    showWhen: { field: 'theme', is: [T.Facts] },
    description: 'e.g. Sunday worship — 10:00 AM.',
  })
  facts!: FactRow[];

  @Paragraph({
    label: 'Address', maxLength: 600, rows: 3,
    showWhen: { field: 'theme', is: [T.Location] },
  })
  address!: string;

  @Link({
    label: 'Map link',
    showWhen: { field: 'theme', is: [T.Location] },
    description: 'A Google Maps link. It opens in a new tab.',
  })
  mapHref!: string;

  @Text({
    label: 'Map link text', maxLength: 120, default: 'Get directions',
    showWhen: { field: 'theme', is: [T.Location] },
  })
  mapLabel!: string;

  @List({
    label: 'Questions', of: () => FaqRow, maxItems: 30,
    showWhen: { field: 'theme', is: [T.Faq] },
  })
  questions!: FaqRow[];

  @Link({
    label: 'YouTube link',
    showWhen: { field: 'theme', is: [T.Video] },
    description: 'Paste the video’s address. It plays on this page.',
  })
  videoHref!: string;

  @List({
    label: 'Pictures', of: () => VisitPictureRow, maxItems: 40,
    showWhen: { field: 'theme', is: [T.Gallery] },
  })
  pictures!: VisitPictureRow[];

  /** On unless somebody says otherwise — see commons/shown for why only a no counts. */
  @Bool({
    label: 'Show this band', default: true,
    description: 'Untick to take it off the page without deleting what is in it.',
  })
  show!: boolean;
}
